import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import type { ActivityLog, Group, SafeUser } from "@shared/schema";
import { useAuth } from "@/lib/auth";
import { apiRequest } from "@/lib/queryClient";
import { describeActivity, involvesMe, whenLabel } from "@/lib/activity";
import { warmAvatar, SELF_AVATAR } from "@/lib/avatar";
import { ActivityRow } from "@/components/History";

/** Where an activity row should take you: its group, or the other friend. */
export function activityHref(row: ActivityLog, meId: string): string | null {
  if (row.groupId) return `/groups/${row.groupId}`;
  const other = (row.participantIds ?? []).find((id) => id !== meId);
  return other ? `/friends/${other}` : null;
}

export function useActivityLookups() {
  const { user } = useAuth();
  const { data: friends = [] } = useQuery<SafeUser[]>({ queryKey: ["/api/friends"] });
  const { data: groups = [] } = useQuery<Group[]>({ queryKey: ["/api/groups"] });
  const { data: members = [] } = useQuery<SafeUser[]>({ queryKey: ["/api/members/all"], enabled: groups.length > 0 });
  const people = new Map<string, string>();
  [...members, ...friends].forEach((p) => people.set(p.id, p.name));
  const nameOf = (id: string) => (id === user?.id ? "You" : people.get(id) ?? "Someone");
  const groupNameOf = (id: string) => groups.find((g) => g.id === id)?.name;
  const colorOf = (id: string) => (id === user?.id ? SELF_AVATAR : warmAvatar(id));
  return { meId: user?.id ?? "", nameOf, groupNameOf, colorOf };
}

// Home's "What changed": the latest few things that involve you.
export function WhatChanged() {
  const [, setLocation] = useLocation();
  const { meId, nameOf, groupNameOf, colorOf } = useActivityLookups();
  const { data = [] } = useQuery<ActivityLog[]>({
    queryKey: ["/api/activity", "home"],
    queryFn: async () => (await apiRequest("GET", "/api/activity?limit=25")).json(),
    enabled: !!meId,
  });
  const rows = data.filter((r) => involvesMe(r, meId)).slice(0, 4);
  if (rows.length === 0) return null;
  return (
    <div data-testid="what-changed">
      <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-muted-foreground mb-2">What changed</p>
      <div className="rounded-[22px] bg-card px-3.5 py-1">
        {rows.map((r) => {
          const href = activityHref(r, meId);
          return (
            <ActivityRow
              key={r.id}
              line={describeActivity(r, meId, nameOf, groupNameOf)}
              when={whenLabel(r.createdAt)}
              initial={(r.userId === meId ? "Y" : r.userName.charAt(0)).toUpperCase()}
              color={colorOf(r.userId)}
              onClick={href ? () => setLocation(href) : undefined}
            />
          );
        })}
      </div>
      <Link href="/activity">
        <span className="block text-center text-sm text-accent-foreground py-3 cursor-pointer" data-testid="see-all-activity">See all activity</span>
      </Link>
    </div>
  );
}
